/**
 * Deterministic colored-initial avatars.
 * Same name → same color always. Mobile-friendly, no network calls.
 */
import { cn } from '../../lib/utils';

const PALETTES = [
  ['bg-emerald-100 text-emerald-800', 'bg-emerald-900/30 dark:text-emerald-300'],
  ['bg-orange-100 text-orange-800', 'bg-orange-900/30 dark:text-orange-300'],
  ['bg-rose-100 text-rose-800', 'bg-rose-900/30 dark:text-rose-300'],
  ['bg-amber-100 text-amber-800', 'bg-amber-900/30 dark:text-amber-300'],
  ['bg-sky-100 text-sky-800', 'bg-sky-900/30 dark:text-sky-300'],
  ['bg-violet-100 text-violet-800', 'bg-violet-900/30 dark:text-violet-300'],
  ['bg-pink-100 text-pink-800', 'bg-pink-900/30 dark:text-pink-300'],
  ['bg-teal-100 text-teal-800', 'bg-teal-900/30 dark:text-teal-300'],
  ['bg-indigo-100 text-indigo-800', 'bg-indigo-900/30 dark:text-indigo-300'],
];

const SIZES = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-14 h-14 text-lg',
  xl: 'w-20 h-20 text-2xl',
};

function hashName(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return Math.abs(h);
}

export function getAvatarColors(name) {
  const idx = hashName(name) % PALETTES.length;
  const [light, dark] = PALETTES[idx];
  return `${light} ${dark}`;
}

export default function Avatar({ name = '?', size = 'md', className, ...rest }) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || '?';
  const palette = getAvatarColors(name);
  return (
    <div
      title={name}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-bold ring-2 ring-background shrink-0',
        SIZES[size] || SIZES.md,
        palette,
        className
      )}
      {...rest}
    >
      {initials}
    </div>
  );
}

/**
 * AvatarStack — overlapping avatars (max N) + "+rest" pill.
 */
export function AvatarStack({ users = [], max = 4, size = 'sm' }) {
  const visible = users.slice(0, max);
  const rest = users.length - visible.length;
  return (
    <div className="flex items-center -space-x-2">
      {visible.map((u, i) => (
        <Avatar key={u.id || u.name || i} name={u.name} size={size} className="ring-2 ring-background" />
      ))}
      {rest > 0 && (
        <div className={cn('inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground font-semibold ring-2 ring-background', SIZES[size] || SIZES.md)}>
          +{rest}
        </div>
      )}
    </div>
  );
}
